import { z } from 'zod';
import { ValidationMessages } from '../admin/guides/validationMessages';
import { GuideDifficulty, GuideLanguage, GuideStepType } from '@/services/apis/guides';

export const createGuideBuilderSchemas = (messages: ValidationMessages) => {
  const stepContentSchema = z.object({
    text: z.string().optional().or(z.literal('')),
    images: z.array(z.string()).optional().or(z.literal('')),
    videos: z.array(z.string()).optional().or(z.literal('')),
    questions: z.array(z.string()).optional().or(z.literal('')),
    pdf: z.string().optional().or(z.literal('')),
    code: z.string().optional().or(z.literal('')),
  });
  // Schema for step title only (used in StepProperties form)
  const stepTitleSchema = z.object({
    title: z.string().min(1, messages.stepTitle.required),
  });
  // Full step schema that extends stepTitleSchema
  const stepSchema = stepTitleSchema.extend({
    id: z.string(),
    content: stepContentSchema.optional(),
    type: z.enum(GuideStepType),
    position: z.number(),
    questionConfig: z
      .object({
        minimumScore: z.number().optional(),
        randomizeOrder: z.boolean().optional(),
        selectRandomCount: z.number().optional(),
      })
      .optional(),
  });
  const titleSchema = {
    title: z
      .string()
      .min(1, messages.title.required)
      .min(3, messages.title.minLength)
      .max(100, messages.title.maxLength),
  };
  const guideTitleSchema = z.object(titleSchema);
  const stepsSchema = z.array(stepSchema);
  const stepsSchemaObject = { steps: stepsSchema };
  const stepsFormSchema = z.object(stepsSchemaObject);

  const guideDetailsFormSchema = z.object({
    id: z.string().optional().or(z.literal('')),
    description: z.string().max(500, messages.description.maxLength).optional().or(z.literal('')),
    difficulty: z.enum(GuideDifficulty, {
      message: messages.difficulty.required,
    }),
    estimatedTime: z.number().max(50, messages.estimatedTime.maxLength).optional().or(z.literal('')),
    language: z.enum(GuideLanguage),
    categoryId: z.string().optional().or(z.literal('')),
    productId: z.string().optional().or(z.literal('')),
    isRecommended: z.boolean().optional(),
  });

  const guideBuilderSchema = guideDetailsFormSchema
    .extend({
      selectedStepId: z.string().optional().or(z.literal(null)),
    })
    .extend(titleSchema)
    .extend(stepsSchemaObject);

  return {
    guideBuilderSchema,
    guideTitleSchema,
    stepTitleSchema,
    stepSchema,
    guideDetailsFormSchema,
    stepsFormSchema,
  };
};

// Fallback schemas with English messages for when translations are not available
const fallbackMessages: ValidationMessages = {
  title: {
    required: 'Title is required',
    minLength: 'Title must be at least 3 characters',
    maxLength: 'Title must be less than 100 characters',
  },
  stepTitle: {
    required: 'Step title is required',
  },
  description: {
    maxLength: 'Description must be less than 500 characters',
  },
  difficulty: {
    required: 'Difficulty level is required',
  },
  estimatedTime: {
    maxLength: 'Estimated time must be less than 50 characters',
  },
  file: {
    required: 'Please select a file',
    invalidType: 'Only PDF files are allowed',
    maxSize: 'File size must be less than 10MB',
  },
  pdfUrl: {
    required: 'Please provide either a file or PDF URL',
    invalidUrl: 'Please enter a valid URL',
    invalidPdfUrl: 'URL must point to a PDF file',
  },
};

export const {
  guideBuilderSchema,
  stepSchema,
  stepTitleSchema,
  guideTitleSchema,
  guideDetailsFormSchema,
  stepsFormSchema,
} = createGuideBuilderSchemas(fallbackMessages);

export type GuideBuilderFormData = z.infer<typeof guideBuilderSchema>;
export type TutorialStepFormData = z.infer<typeof stepSchema>;
export type GuideDetailsFormData = z.infer<typeof guideDetailsFormSchema>;
export type GuideTitleFormData = z.infer<typeof guideTitleSchema>;
export type StepsFormData = z.infer<typeof stepsFormSchema>;
export type StepPropertiesFormData = z.infer<typeof stepTitleSchema>;
export type TransformedStep = z.infer<typeof stepSchema>;
